import React, { useEffect } from 'react';
import csv from 'csvtojson';
import { Table, TableWrapper, Cell } from 'react-native-table-component';
import { Box, Text, Input, View, Button, HStack, useColorModeValue } from 'native-base';
import { TouchableOpacity } from 'react-native';
import { textStyle } from 'src/styles/textStyle';
import { tableStyle } from 'src/styles/tableStyle';
import { useDispatch, useSelector } from 'react-redux';
import { IStore } from 'src/redux/reducers';
import * as JobActions from '../../redux/actions/jobAction';
import { isElectron } from 'src/utils/miscUtils';
import { getText } from 'src/utils/i18n';
import { localizeResultCell, localizeResultLabel } from 'src/utils/resultLocalization';
import { filterRowsByRejectSnapshot } from 'src/utils/rejectConfig';
import { useResponsive } from 'src/hooks/useResponsive';

export interface IDisplayCsvTableComponentProps {
  jobId?: string;
  result_field?: string;
  title: string;
  // indexes based on the table data or csv file
  columnsToDisplay: number[];
  tableHead?: any;
  tableData?: any;
  csvFileUrl?: string | undefined;
  isEditable?: boolean;
  // indexes based on the displayed columns
  editableColumns?: number[];
  total_kernels?: number;
  rejectionMapping?: Record<string, string>;
  rejectionCalculationConfig?: {
    particleDistribution?: {
      selectedKeys?: string[];
      selectedLabels?: string[];
    };
    rejectedGrains?: {
      selectedKeys?: string[];
      selectedLabels?: string[];
    };
  };
}

const getRejectSnapshotForResultField = (
  resultField: string | undefined,
  rejectionCalculationConfig: IDisplayCsvTableComponentProps['rejectionCalculationConfig']
) => {
  if (!rejectionCalculationConfig) return undefined;
  const field = String(resultField || '');
  if (field.includes('particle_distribution')) {
    return rejectionCalculationConfig.particleDistribution;
  }
  return undefined;
};

export default function DisplayCsvTableComponentV2(props: IDisplayCsvTableComponentProps) {

  const dispatch = useDispatch();
  const { isMobile } = useResponsive();
  const colorMode = useSelector((store: IStore) => store.settings.colorMode);
  const isOnline = useSelector((store: IStore) => store.app.isOnline);
  const locale = useSelector((store: IStore) => store.app.locale);
  const tableHeadBg = useColorModeValue('#f1f8ff', '#2d3748');
  const tableBorderColor = useColorModeValue('#c8e1ff', '#4a5568');
  const titleTextColor = useColorModeValue('coolGray.800', 'coolGray.100');

  const [state, setState] = React.useState<{
    title: string;
    columns: number[];
    tableHead: any[];
    showHead: boolean;
    tableData: any[][];
  }>({
    title: "",
    columns: [],
    tableHead: [],
    showHead: true,
    tableData: []
  });

  const [isEditing, setIsEditing] = React.useState(false);
  const [sortByColumn, setSortByColumn] = React.useState<string | null>(null);
  const [sortOrder, setSortOrder] = React.useState<"asc" | "desc">("asc");
  const [csvLoadError, setCsvLoadError] = React.useState<string | null>(null);
  const mobileHeaderFontSize = 11;
  const mobileCellFontSize = 12;
  const isRejectionAnalysisTable = props.title === getText('rejectionAnalysis')
    || String(props.result_field || '').includes('rejection_analysis')
    || String(props.csvFileUrl || '').includes('rejection_analysis');

  useEffect(() => {
    if (props.csvFileUrl != null && props.csvFileUrl != undefined && props.csvFileUrl != "") {
      setTableData(props.title, props.columnsToDisplay || [], props.csvFileUrl);
    } else {
      setTableDataUsingProps()
    }
  }, [props.csvFileUrl, props.title, props.columnsToDisplay, props.result_field, props.rejectionCalculationConfig, props.rejectionMapping]);

  useEffect(() => {
    setTableDataUsingProps()
  }, [props.tableData, props.rejectionCalculationConfig, props.rejectionMapping]);

  const readCsvText = async (csvFileUrl: string) => {
    if (isElectron() && (window as any).electron?.fetchFileContent) {
      return (window as any).electron.fetchFileContent(csvFileUrl);
    }

    if (csvFileUrl.startsWith('file://')) {
      if (!isElectron() || !(window as any).electron?.readLocalFile) {
        throw new Error('Local file access not available');
      }
      return (window as any).electron.readLocalFile(csvFileUrl);
    }

    if (!isOnline) {
      throw new Error('Internet connection required');
    }

    const response = await fetch(csvFileUrl);
    return response.text();
  };

  const setTableData = async (title: string, cols: any, csvFileUrl: string) => {
    try {
      setCsvLoadError(null);
      const resp = await readCsvText(csvFileUrl);
      const csvRow = await csv({
        noheader: true,
        output: "csv"
      }).fromString(resp);
      const rejectSnapshot = getRejectSnapshotForResultField(props.result_field, props.rejectionCalculationConfig);
      const grain = props.rejectionMapping ? { rejectionMapping: props.rejectionMapping } as any : undefined;
      const tableRows = rejectSnapshot
        ? filterRowsByRejectSnapshot(csvRow.slice(1), rejectSnapshot, grain)
        : csvRow.slice(1);

      setState((prevState) => ({
        ...prevState,
        title: title,
        columns: cols || Array.from(Array(csvRow[0].length).keys()),
        tableHead: csvRow[0],
        tableData: tableRows
      }));
    } catch (error) {
      if (!isOnline && csvFileUrl && !csvFileUrl.startsWith('file://')) {
        setCsvLoadError(getText('offlineTableLoad'));
      } else {
        setCsvLoadError(getText('failedTableLoad'));
      }
      console.error("some error occurred", error);
    }
  }

  const setTableDataUsingProps = () => {
    const rejectSnapshot = getRejectSnapshotForResultField(props.result_field, props.rejectionCalculationConfig);
    const grain = props.rejectionMapping ? { rejectionMapping: props.rejectionMapping } as any : undefined;
    const tableData = rejectSnapshot
      ? filterRowsByRejectSnapshot(props.tableData || [], rejectSnapshot, grain)
      : props.tableData || [];
    setState((prevState) => ({
      ...prevState,
      title: props.title,
      columns: props.columnsToDisplay,
      tableHead: props.tableHead || [],
      tableData,
    }));
  }

  const beautify = (data: any) => {
    if (!isNaN(parseFloat(data)) && !isNaN(data - 0)) {
      let floatData = parseFloat(data);
      if (Math.ceil(floatData) == Math.floor(floatData)) {
        return data;
      } else {
        return floatData.toFixed(2);
      }
    } else {
      return data;
    }
  }

  const sortItems = (column: string) => {
    if (sortByColumn === column) {
      const newSortOrder = sortOrder === 'asc' ? 'desc' : 'asc';
      setSortOrder(newSortOrder);
      applySorting(column, newSortOrder);
    } else {
      setSortByColumn(column);
      setSortOrder("asc");
      applySorting(column, 'asc');
    }
  }

  const applySorting = (column: string, order: "asc" | "desc") => {
    const columnIndex = state.tableHead.indexOf(column);
    if (columnIndex === -1) return;

    const sortedData = [...state.tableData].sort((a: any, b: any) => {
      const aVal = a[columnIndex];
      const bVal = b[columnIndex];

      // Handle boolean-like strings
      if (aVal === 'True' || aVal === 'False' || bVal === 'True' || bVal === 'False') {
        return order === 'asc'
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }

      // Handle numeric values
      const aNum = parseFloat(aVal);
      const bNum = parseFloat(bVal);
      if (!isNaN(aNum) && !isNaN(bNum)) {
        return order === 'asc' ? aNum - bNum : bNum - aNum;
      }

      // Handle string values
      return order === 'asc'
        ? String(aVal).localeCompare(String(bVal))
        : String(bVal).localeCompare(String(aVal));
    });

    setState((prevState) => ({
      ...prevState,
      tableData: sortedData
    }));
  }

  const updateTableData = (rowIndex: number, colIndex: number, text: string) => {
    setState((prevState) => {
      const tableData = [...prevState.tableData];
      tableData[rowIndex] = [...tableData[rowIndex]];
      tableData[rowIndex][prevState.columns[colIndex]] = text;

      // custom logic to update the table data based on result field
      if (props.result_field === "rejection_analysis_csv") {
        if (colIndex === 1) {
          tableData[rowIndex][prevState.columns[colIndex + 1]] = (parseFloat(text) / props.total_kernels! * 100).toFixed(2);
        }
      }

      return {
        ...prevState,
        tableData: tableData
      };
    });
  }

  const inputElement = (data: string, rowIndex: number, colIndex: number) => (

    <Input selectTextOnFocus
      scrollEnabled={false}
      key={`${rowIndex},${colIndex}`}
      value={
        colIndex === 0 ? data
          : (isEditing ? data : beautify(data))
      }
      style={
        [{
          color: colorMode == "dark" ? "white" : "black",
          fontSize: isMobile && isRejectionAnalysisTable ? mobileCellFontSize : 15,
          textAlign: isMobile && isRejectionAnalysisTable ? 'center' : 'left',
          width: isMobile && isRejectionAnalysisTable ? '100%' : undefined,
          paddingHorizontal: isMobile && isRejectionAnalysisTable ? 4 : 8,
        }]
      }
      keyboardType={"decimal-pad"}
      onChangeText={(text) => {
        updateTableData(rowIndex, colIndex, text);
      }} />
  );

  const updateResults = async () => {
    if (props.jobId === undefined
      || props.jobId === ""
      || props.result_field === undefined
      || props.result_field === "") {
      return;
    }

    const tableDataJson: any[] = [];

    for (let i = 0; i < state.tableData.length; i++) {
      const rowData: any = {};
      for (let j = 0; j < state.tableData[i].length; j++) {
        rowData[state.tableHead[j]] = state.tableData[i][j];
      }
      tableDataJson.push(rowData);
    }

    dispatch(JobActions.updateJobResult.request({
      "jobId": props.jobId,
      "data": tableDataJson,
      "result_field": props.result_field
    }));
  }

  const textElement = (data: any) => (
    <Text style={[tableStyle.cellText, {
      color: colorMode === 'dark' ? 'white' : 'black',
      fontSize: isMobile && isRejectionAnalysisTable ? mobileCellFontSize : 15,
      marginHorizontal: isMobile && isRejectionAnalysisTable ? 6 : 10,
      marginVertical: isMobile && isRejectionAnalysisTable ? 8 : 10,
      textAlign: isMobile && isRejectionAnalysisTable ? 'center' : 'left',
      width: isMobile && isRejectionAnalysisTable ? '100%' : undefined,
    }]}>{localizeResultCell(data, { locale })}</Text>
  );

  const getHeaderLabel = (label: any) => {
    if (!(isMobile && isRejectionAnalysisTable)) {
      return localizeResultLabel(label, { locale });
    }

    switch (String(label)) {
      case 'Percent by count':
      case '% by count':
        return getText('particleDistPercentCountShort');
      case 'Percent by weight':
      case '% by weight':
        return getText('particleDistPercentWeightShort');
      default:
        return localizeResultLabel(label, { locale });
    }
  };

  const rejectionHeaderTextStyle = [
    tableStyle.cellText,
    {
      color: colorMode === 'dark' ? 'white' : 'black',
      textAlign: 'center' as const,
      fontSize: isMobile && isRejectionAnalysisTable ? mobileHeaderFontSize : 15,
      fontWeight: '600' as const,
      marginHorizontal: isMobile && isRejectionAnalysisTable ? 4 : 10,
      marginVertical: isMobile && isRejectionAnalysisTable ? 6 : 10,
    }
  ];

  const headElement = (data: any) => (
    <TouchableOpacity onPress={() => sortItems(data)}>
      <HStack space={1} alignItems="center">
        <Text style={[tableStyle.cellText, {
          color: colorMode === 'dark' ? 'white' : 'black',
        }]}>{localizeResultLabel(data, { locale })}</Text>
        {sortByColumn === data && (
          <Text style={[tableStyle.cellText, {
            color: colorMode === 'dark' ? 'white' : 'black',
          }]}>{sortOrder === 'asc' ? '↑' : '↓'}</Text>
        )}
      </HStack>
    </TouchableOpacity>
  );

  return (
    <Box>
      <View style={{
        flexDirection: 'row',
        justifyContent: 'space-between',
      }}>
        <Text color={titleTextColor} style={textStyle.tableTitleText}>{props.title}</Text>
        {props.isEditable
          && <Button variant={"link"} onPress={async () => {
            if (isEditing) {
              await updateResults();
            }

            setIsEditing(!isEditing);
          }}>{isEditing ? getText('saveChanges') : getText('edit')}</Button>}
      </View>
      {csvLoadError && (
        <Text mt={1} fontSize="xs" color="coolGray.500">
          {csvLoadError}
        </Text>
      )}
      <Table borderStyle={{ borderWidth: 2, borderColor: tableBorderColor }}>
        <TableWrapper style={[
          tableStyle.head,
          { backgroundColor: tableHeadBg },
          isMobile && isRejectionAnalysisTable && { height: 48 }
        ]}>
          {
            state.tableHead?.filter((cellData, index) => {
              return state.columns.includes(index);
            }).map((cellData, index) => (
              <Cell
                key={index}
                data={isMobile && isRejectionAnalysisTable ? getHeaderLabel(cellData) : headElement(cellData)}
                textStyle={isMobile && isRejectionAnalysisTable ? rejectionHeaderTextStyle : undefined}
              />
            )) as any
          }
        </TableWrapper>
        {
          (state.tableData || []).map((rowData, index) => {
            return (
              <TableWrapper key={index} style={[tableStyle.row, isMobile && isRejectionAnalysisTable && { minHeight: 42 }]}>
                {
                  rowData?.filter((rowData: any, index: number) => {
                    return state.columns.includes(index);
                  }).map((cellData: any, cellIndex: number) => (
                    <Cell
                      key={cellIndex}
                      data={
                        (!props.editableColumns?.includes(cellIndex) || !isEditing) ? textElement(beautify(cellData)) :
                          inputElement(cellData, index, cellIndex)
                      } />
                  )) as any
                }
              </TableWrapper>
            )
          }) as any
        }
      </Table>
    </Box>
  );
};
